import React, { useState, useEffect } from 'react';
import { Table, Alert, Spinner, Card } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import CardioDataFilters from './CardioDataFilters';
import {
  calculatePace,
  formatPace,
  formatDuration,
  formatDistance,
  paceStringToSeconds
} from '../utils/paceCalculations';

function CardioDataTable() {
  // State management
  const [cardioData, setCardioData] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [filters, setFilters] = useState({
    cardioType: 'all',
    exerciseId: null,
    dateRange: 'all',
    startDate: null,
    endDate: null
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'workout_date',
    direction: 'desc'
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExercises, setIsLoadingExercises] = useState(true);
  const [error, setError] = useState(null);

  // Fetch exercises on mount
  useEffect(() => {
    fetchExercises();
  }, []);

  // Fetch cardio data when filters change
  useEffect(() => {
    if (!isLoadingExercises) {
      fetchCardioData();
    }
  }, [filters, isLoadingExercises]);

  const fetchExercises = async () => {
    try {
      setIsLoadingExercises(true);
      const data = await workoutAPI.fetchExercises();
      setExercises(data);
    } catch (err) {
      console.error('Error fetching exercises:', err);
      setError('Failed to load exercises.');
    } finally {
      setIsLoadingExercises(false);
    }
  };

  const fetchCardioData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await workoutAPI.fetchCardioData(filters);
      setCardioData(data.sessions || []);
    } catch (err) {
      console.error('Error fetching cardio data:', err);
      setError('Failed to load cardio data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    const sorted = [...cardioData];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'workout_date':
          aValue = new Date(`${a.workout_date} ${a.workout_time || '00:00:00'}`);
          bValue = new Date(`${b.workout_date} ${b.workout_time || '00:00:00'}`);
          break;
        case 'exercise_name':
          aValue = a.exercise_name || '';
          bValue = b.exercise_name || '';
          break;
        case 'distance':
          aValue = a.distance || a.total_distance || 0;
          bValue = b.distance || b.total_distance || 0;
          break;
        case 'duration':
          aValue = a.duration_seconds || a.total_duration || 0;
          bValue = b.duration_seconds || b.total_duration || 0;
          break;
        case 'pace':
          const aPace = calculatePace(
            a.distance || a.total_distance,
            a.distance_unit,
            a.duration_seconds || a.total_duration
          );
          const bPace = calculatePace(
            b.distance || b.total_distance,
            b.distance_unit,
            b.duration_seconds || b.total_duration
          );
          aValue = aPace || Infinity;
          bValue = bPace || Infinity;
          break;
        case 'heart_rate':
          aValue = a.heart_rate_avg || a.avg_heart_rate || 0;
          bValue = b.heart_rate_avg || b.avg_heart_rate || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  };

  const toggleRowExpansion = (exerciseLogId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(exerciseLogId)) {
      newExpanded.delete(exerciseLogId);
    } else {
      newExpanded.add(exerciseLogId);
    }
    setExpandedRows(newExpanded);
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort ms-1 text-muted"></i>;
    }
    return sortConfig.direction === 'asc' ?
      <i className="fas fa-sort-up ms-1"></i> :
      <i className="fas fa-sort-down ms-1"></i>;
  };

  const renderCardioRow = (session) => {
    const distance = session.distance || session.total_distance;
    const duration = session.duration_seconds || session.total_duration;
    const heartRate = session.heart_rate_avg || session.avg_heart_rate;
    const isInterval = session.cardio_type === 'cardio_segments';
    const isExpanded = expandedRows.has(session.exercise_log_id);

    const pace = calculatePace(distance, session.distance_unit, duration);
    const paceFormatted = formatPace(pace);

    return (
      <React.Fragment key={session.exercise_log_id}>
        <tr
          onClick={() => isInterval && toggleRowExpansion(session.exercise_log_id)}
          style={isInterval ? { cursor: 'pointer' } : {}}
        >
          <td>
            {isInterval && (
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
            )}
            {new Date(session.workout_date).toLocaleDateString()}
          </td>
          <td>{session.exercise_name}</td>
          <td>
            {distance ? formatDistance(distance, session.distance_unit) : 'N/A'}
            {isInterval && session.segment_count && (
              <small className="text-muted d-block">
                {session.segment_count} segments
              </small>
            )}
          </td>
          <td>{formatDuration(duration)}</td>
          <td>{paceFormatted} min/mile</td>
          <td>{Math.round(heartRate) || '-'}</td>
        </tr>

        {/* Segment breakdown for interval workouts */}
        {isInterval && isExpanded && session.segments && session.segments.length > 0 && (
          <tr>
            <td colSpan="6" className="p-0">
              <div className="segment-breakdown bg-light p-3">
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th width="10%">#</th>
                      <th width="25%">Distance</th>
                      <th width="20%">Time</th>
                      <th width="20%">Pace</th>
                      <th width="15%">Avg HR</th>
                      <th width="10%">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {session.segments.map((seg) => {
                      const segPace = calculatePace(seg.distance, seg.distance_unit, seg.duration_seconds);
                      return (
                        <tr key={seg.segment_number}>
                          <td>{seg.segment_number}</td>
                          <td>{seg.distance ? formatDistance(seg.distance, seg.distance_unit) : 'N/A'}</td>
                          <td>{formatDuration(seg.duration_seconds)}</td>
                          <td>{formatPace(segPace)} min/mile</td>
                          <td>{seg.heart_rate_avg || '-'}</td>
                          <td><small>{seg.notes || '-'}</small></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  if (isLoadingExercises) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading exercises...</p>
      </div>
    );
  }

  return (
    <div className="cardio-data-table">
      <Card>
        <Card.Body>
          <CardioDataFilters
            filters={filters}
            onFilterChange={setFilters}
            exercises={exercises}
          />

          {error && <Alert variant="danger">{error}</Alert>}

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading cardio data...</p>
            </div>
          ) : cardioData.length === 0 ? (
            <Alert variant="info">
              <i className="fas fa-info-circle me-2"></i>
              {filters.cardioType !== 'all' || filters.exerciseId || filters.dateRange !== 'all'
                ? 'No cardio workouts found matching your filters. Try adjusting your filter criteria.'
                : 'No cardio workouts logged yet. Start logging cardio workouts to see them here!'}
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort('workout_date')}
                      style={{ cursor: 'pointer' }}
                    >
                      Date {renderSortIcon('workout_date')}
                    </th>
                    <th
                      onClick={() => handleSort('exercise_name')}
                      style={{ cursor: 'pointer' }}
                    >
                      Exercise {renderSortIcon('exercise_name')}
                    </th>
                    <th
                      onClick={() => handleSort('distance')}
                      style={{ cursor: 'pointer' }}
                    >
                      Distance {renderSortIcon('distance')}
                    </th>
                    <th
                      onClick={() => handleSort('duration')}
                      style={{ cursor: 'pointer' }}
                    >
                      Total Time {renderSortIcon('duration')}
                    </th>
                    <th
                      onClick={() => handleSort('pace')}
                      style={{ cursor: 'pointer' }}
                    >
                      Avg Pace {renderSortIcon('pace')}
                    </th>
                    <th
                      onClick={() => handleSort('heart_rate')}
                      style={{ cursor: 'pointer' }}
                    >
                      Avg HR {renderSortIcon('heart_rate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedData().map(session => renderCardioRow(session))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default CardioDataTable;
